import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NotificationService } from '../../../services/notification.service';
import { INotification } from '../../../interfaces';
import { CommonModule } from '@angular/common';
import { DirectionService } from '../../../services/direction.service';
import { SinceDatePipe } from '../../../pipes/since-date.pipe';
import { ButtonModule } from 'primeng/button';
import { Router, RouterModule } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { ToastrService } from 'ngx-toastr';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [CommonModule, TranslateModule, SinceDatePipe, ButtonModule, RouterModule, DialogModule],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.scss'
})
export class NotificationsComponent implements OnInit, OnDestroy {
  @Input() notifications = [] as any[]
  @Input() selectedStrategy = {} as any
  @Input() isMarkAllReadBtn = true;
  dir!: 'rtl' | 'ltr';
  selectedNotificaton = {} as any
  visible = false;

  constructor(
    private notificationService: NotificationService,
    private dirService: DirectionService,
    private router: Router,
    private toastr: ToastrService,

  ) {
  }

  ngOnDestroy(): void {
    this.visible = false;
  };

  ngOnInit(): void {
    this.dirService.direction$.subscribe(dir => {
      this.dir = dir;
    })
    this.notificationService.openNotificationSidebar$.subscribe(status => {
      if (!status) {
        this.visible = false;
      }
    })
  }

  markAsRead(notificationId: number) {
    const notification = this.notifications.find(n => n.id == notificationId) as INotification
    if (notification.is_read) {
      return;
    } else {

      this.notificationService.markAsRead(notificationId).subscribe(() => {
        notification["is_read"] = true
      })
    }
  };

  // Handle notification click - mark as read and navigate
  handleNotificationClick(notification: INotification) {
    this.markAsRead(notification.id);
    this.navigateToNotificationTarget(notification);
  };

  selectNotification(notificationId: number) {
    this.visible = true;
    this.selectedNotificaton = this.notifications.find(x => x.id == notificationId) as any;
  };

  // navigate to link 
  navigateToEntity() {
    this.visible = false;
    this.navigateToNotificationTarget(this.selectedNotificaton);
  };

  // Navigate to notification target page
  private navigateToNotificationTarget(notification: INotification) {
    const type = notification.type;
    const data = notification.data;
    const strategyId = notification.strategic_plan_id || this.selectedStrategy.id || 1;

    switch (type) {
      // Project submission request - navigate to project review page
      case 'submission_request':
        if (data?.id) {
          this.router.navigate(['/strategies', strategyId, 'projects', data.id], {
            state: { hasAccess: true, scrollToReview: true }
          });
        }
        break;

      // Project submission response - navigate to project to see results
      case 'submission_response':
        if (data?.id || data?.parentEntityId) {
          this.router.navigate(['/strategies', strategyId, 'projects', data.id || data.parentEntityId], {
            state: { hasAccess: true, scrollToReview: true }
          });
        }
        break;

      // Milestone audit request - navigate to file auditor
      case 'milestone_output_audit_request':
        if (data?.parentEntityId && data?.subEntityId) {
          this.router.navigate(['/project-file-auditor'], {
            queryParams: { 
              strategyId: strategyId, 
              parentEntityId: data.parentEntityId, 
              subEntityId: data.subEntityId 
            }
          });
        }
        break;

      // Milestone audit response - navigate to project
      case 'milestone_output_audit_response':
        if (data?.parentEntityId) {
          this.router.navigate(['/strategies', strategyId, 'projects', data.parentEntityId], {
            state: { hasAccess: true }
          });
        }
        break;

      // Indicator outcome proof request - navigate to indicator auditor
      case 'outcome_proof_audit_request':
        if (data?.parentEntityId && data?.subEntityId) {
          this.router.navigate(['/indicator-FileAuditsingle'], {
            queryParams: { 
              strategyId: strategyId, 
              parentEntityId: data.parentEntityId, 
              subEntityId: data.subEntityId 
            }
          });
        }
        break;

      // Indicator outcome proof response - navigate to indicator
      case 'outcome_proof_audit_response':
        if (data?.parentEntityId) {
          this.router.navigate(['/strategies', strategyId, 'indicators', data.parentEntityId]);
        }
        break;

      // Project execution stop/resume - navigate to project
      case 'project_execution_stop':
      case 'project_execution_resume':
        if (data?.id) {
          this.router.navigate(['/strategies', strategyId, 'projects', data.id], {
            state: { hasAccess: true }
          });
        }
        break;

      // Approval notifications - navigate to project
      case 'approval_message':
      case 'SectionAdminApproved':
      case 'SectionAdminReject':
      case 'DepartmentAdminApproved':
      case 'DepartmentAdminReject':
      case 'SectorAdminApproved':
      case 'SectorAdminReject':
        if (data?.id) {
          this.router.navigate(['/strategies', strategyId, 'projects', data.id], {
            state: { hasAccess: true }
          });
        }
        break;

      // Milestone due reminder - navigate to project
      case 'milestone_due_reminder':
        if (data?.parentEntityId) {
          this.router.navigate(['/strategies', strategyId, 'projects', data.parentEntityId], {
            state: { hasAccess: true }
          });
        }
        break;

      default:
        console.warn('No navigation route defined for notification type:', type);
    }
  };

  selectAllNotificationsASRead() {
    this.notificationService.markAllNotificationsASRead(this.selectedStrategy.id).subscribe((response: any) => {
      this.toastr.success(response.data.message);
      this.notifications = this.notifications.map(n => {
        return { ...n, is_read: true }
      })
    })
  };

  deleteNotification(notificationId: number) {
    this.notificationService.deleteNotification(notificationId).subscribe((response: any) => {
      this.toastr.success(response.data.message || 'Notification deleted successfully');
      this.notifications = this.notifications.filter(n => n.id !== notificationId);
    }, (error) => {
      this.toastr.error(error?.error?.data?.message || 'Error deleting notification');
    });
  }
};
